import { SearchOutlined } from '@ant-design/icons';
import { ConfigProvider, Layout, Flex, Skeleton, Row, Col, Select, DatePicker, Button, Table, notification, TableProps, Card } from 'antd';
import { SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LoaderSpinner } from '../../components/common/Loader';
import { getReportFilterList, getReportData, exportReportData } from '../../service/httpsCalls';
import { createTableConfig } from "../Billing/tableConfig";
const { RangePicker } = DatePicker;

/**
 * Reports Route Component
 * Displays reports page content.
 */

interface PaginationObject {
    totalPages: number,
    loading: boolean,
    pageSize: number,
    currentPage: number,
    sortInfo: SorterResult<any>
}

interface ReportType {
    reportConfig : string,
    reportId : number,
    reportName : string
}
type NotificationType = 'success' | 'info' | 'warning' | 'error';

const Reports: React.FC = () => {
    const [api, contextHolder] = notification.useNotification();
    const [filterLoader, setFilterLoader] = useState(false);
    const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
    const [selectedReportType, setSelectedReportType] = useState<string>(null);
    const [tableKey, setTableKey] = useState(0); // Key state to refresh table
    const [menuVisible, setMenuVisible] = useState(false);
    const [primaryKey, setPrimaryKey] = useState<string | null>(null);
    const [reportConfig, setReportConfig] = useState<string>('');
    const [columns, setColumns] = useState<any>([]);
    const [reportData, setReportData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [tableHeight, setTableHeight] = useState(300);
    const [filterConfig, setFilterConfig] = useState<any>();
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [calendarRange, setCalendarRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    let paginationObject: PaginationObject = {
        totalPages: 0,
        loading: false,
        pageSize: 50,
        currentPage: 1,
        sortInfo: {}
    }
    const [tablePagination, setTablePagination] = useState(paginationObject);
    const [exportLoader, setExportLoader] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[type]({
            message: message,
            description: description
        });
    };

    const dateFilters = useMemo(() => {
        if (!filterConfig?.DateFilters) {
            return undefined;
        }
        const rawFilters = filterConfig.DateFilters;
        return {
            isEnabled: rawFilters.isEnabled ?? rawFilters.enable ?? false,
            hasRestriction: rawFilters.hasRestriction ?? rawFilters.dateRestriction ?? false,
            pastDaysLimit: rawFilters.pastDaysLimit ?? rawFilters.min ?? null,
            futureDaysLimit: rawFilters.futureDaysLimit ?? rawFilters.max ?? null,
            range: typeof rawFilters.range === "number"
                ? rawFilters.range
                : (typeof rawFilters.rangeLimit === "number" ? rawFilters.rangeLimit : 0)
        };
    }, [filterConfig]);

    const isDateFilterEnabled = !!dateFilters?.isEnabled;

    const minSelectableDate = useMemo(() => {
        if (!dateFilters?.hasRestriction) {
            return undefined;
        }
        const today = dayjs().startOf('day');
        if (typeof dateFilters.pastDaysLimit === 'number' && dateFilters.pastDaysLimit >= 0) {
            return today.subtract(dateFilters.pastDaysLimit, 'day');
        }
        return today;
    }, [dateFilters]);

    const maxSelectableDate = useMemo(() => {
        if (!dateFilters?.hasRestriction) {
            return undefined;
        }
        const today = dayjs().startOf('day');
        if (typeof dateFilters.futureDaysLimit === 'number' && dateFilters.futureDaysLimit >= 0) {
            return today.add(dateFilters.futureDaysLimit, 'day');
        }
        return today;
    }, [dateFilters]);

    const normalizeRange = useCallback((rangeStart: Dayjs | null, rangeEnd: Dayjs | null): [Dayjs | null, Dayjs | null] => {
        if (rangeStart && rangeEnd && rangeStart.isAfter(rangeEnd, 'day')) {
            return [rangeEnd, rangeStart];
        }
        return [rangeStart, rangeEnd];
    }, []);

    const disabledDate = useCallback((current: Dayjs) => {
        if (!current) {
            return false;
        }

        if (!dateFilters) {
            return false;
        }

        if (minSelectableDate && current.isBefore(minSelectableDate, 'day')) {
            return true;
        }
        if (maxSelectableDate && current.isAfter(maxSelectableDate, 'day')) {
            return true;
        }

        if (typeof dateFilters.range === 'number' && dateFilters.range > 0) {
            const [rangeStart, rangeEnd] = calendarRange;
            const anchor = rangeStart ?? rangeEnd;
            if (anchor) {
                if (Math.abs(anchor.diff(current, 'day')) > dateFilters.range) {
                    return true;
                }
            }
        }

        return false;
    }, [calendarRange, dateFilters, minSelectableDate, maxSelectableDate]);
    function getFilters() {
        setFilterLoader(true);
        getReportFilterList((res) => {
            setFilterLoader(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Reports are not present!!!'
                openNotificationWithIcon("error", errorMsg, "");
            } else {
                setReportTypes(res)
            }
        })
    }

    function search(pageNumber?: number) {
        setTablePagination({
            ...tablePagination,
            loading: true
        });
        
        getReportData({
            reportId: selectedReportType,
            startDate: startDate ? startDate.startOf('day').format("YYYY-MM-DD") : null,
            endDate: endDate ? endDate.startOf('day').format("YYYY-MM-DD") : null,
            pageNumber: pageNumber ? pageNumber : tablePagination.currentPage,
            pageSize: tablePagination.pageSize
        }, (res) => {
            setShowTable(true);
            if (res?.totalRecords) {
                setReportData(res.items);
                const totalPage = Math.ceil(res.totalRecords / (tablePagination.pageSize / tablePagination.pageSize))
                setTablePagination({
                    ...tablePagination,
                    totalPages: totalPage,
                    currentPage: pageNumber ? pageNumber : tablePagination.currentPage,
                    loading: false
                })
            } else {
                openNotificationWithIcon("info", "No data to display.", "");
                setReportData([]);
                setTablePagination({
                    ...tablePagination,
                    loading: false
                });
            }
        })
    }

    function resetFilters() {
        setSelectedReportType(null);
        setReportConfig('');
        setColumns([]);
        setReportData([]);
        setLoading(false);
        setStartDate(null);
        setEndDate(null);
        setCalendarRange([null, null]);
        setShowTable(false);
        setTablePagination({
            ...tablePagination,
            currentPage: 1,
            totalPages: 1,
            loading: false
        })
    }

    const tableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        if (extra.action == 'sort') {
            return;
        }
        search(pagination.current);
    }
    const updateTableHeight = useCallback(() => {
        let animationFrameId: number | null = null;
        let resizeObserver: ResizeObserver | null = null;

        const measureHeight = () => {
            const container = document.getElementById("table-container");
            const headerElement = container?.getElementsByClassName("ant-table-header")[0] as HTMLElement | undefined;

            if (!container || !headerElement) {
                animationFrameId = window.requestAnimationFrame(measureHeight);
                return;
            }

            const containerHeight = container.clientHeight ?? 0;
            const headerHeight = headerElement.clientHeight ?? 0;

            if (containerHeight === 0 || headerHeight === 0) {
                animationFrameId = window.requestAnimationFrame(measureHeight);
                return;
            }

            setTableHeight(Math.max(containerHeight - headerHeight - 40, 0));
        };

        const attachResizeObserver = () => {
            const container = document.getElementById("table-container");
            const headerElement = container?.getElementsByClassName("ant-table-header")[0] as HTMLElement | undefined;

            if (!container || !headerElement) {
                animationFrameId = window.requestAnimationFrame(attachResizeObserver);
                return;
            }

            if (typeof ResizeObserver !== "undefined") {
                resizeObserver = new ResizeObserver(() => measureHeight());
                resizeObserver.observe(container);
                resizeObserver.observe(headerElement);
            }

            measureHeight();
        };

        const handleResize = () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            measureHeight();
        };

        window.addEventListener("resize", handleResize);
        attachResizeObserver();

        return () => {
            window.removeEventListener("resize", handleResize);
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
            resizeObserver?.disconnect();
        };
    }, []);

    function exportReport() {
        setExportLoader(true);
        exportReportData({
            reportId: selectedReportType,
            startDate: startDate ? startDate.startOf('day').format("YYYY-MM-DD") : null,
            endDate: endDate ? endDate.startOf('day').format("YYYY-MM-DD") : null
        }, (res) => {
            setExportLoader(false);
            openNotificationWithIcon("success", "You will receive the report at your registered email address.", "");
        })
    }

    function getSelectedReportData(id) {
        setReportData([]);
        setShowTable(false);
        setColumns([]);
        setTablePagination({
            ...tablePagination,
            currentPage: 1
        });
        setSelectedReportType(id);
        setStartDate(null);
        setEndDate(null);
        setCalendarRange([null, null]);
        const rep = reportTypes.find((x) => x.reportId == id);
        if (rep) {
            const tableConfig = rep?.reportConfig;
            setReportConfig(tableConfig)
            setFilterConfig(JSON.parse(tableConfig).Filters);
            setPrimaryKey(JSON.parse(tableConfig).PrimaryKey)
        }
    }

    const handleTableChange = (value, record, field) => {
        // Your logic here
        const index = reportData.findIndex(item => item[primaryKey] === record[primaryKey]);
        if (index !== -1) {
            reportData[index][field] = value? value.format('YYYY-MM-DDTHH:mm:ss'):'';
            setReportData([...reportData]);
        }
    };
    //Use Effects
    useEffect(() => {
        getFilters();
        document.title = "SSRS Reports";
    }, [])

    useEffect(() => {
        if (!columns?.length || !showTable || !reportData?.length) {
            return;
        }

        const cleanup = updateTableHeight();
        return cleanup;
    }, [columns, reportData?.length, showTable, updateTableHeight]);
    useEffect(() => {
        if (reportData?.length) {
            if (!columns?.length) {
                const cols = createTableConfig(JSON.parse(reportConfig).Columns, handleTableChange) || [];
                setColumns(cols);
            }
        }
    }, [reportData]);

  return (
      <ConfigProvider theme={{
          components: {
              Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
          }
      }} >
          {contextHolder}
          <Layout style={{
              padding: '20px',
              marginBottom: '5px',
              marginTop: '5px',
              width: '100%'
          }}>
              <h3 style={{ textTransform: 'uppercase', marginTop: '0px' }}>Reports</h3>
              <Flex justify='space-between' style={{ padding: "1rem", alignItems: "center", background: '#fff' }}>
                  <label><SearchOutlined />&nbsp;Search</label>  
              </Flex>
              <Skeleton loading={filterLoader} active>
                  <Row type="flex" style={{ padding: '1rem', paddingTop: "0px", background: '#fff' }}>
                      <Col span={6}>
                          <Select
                              style={{ width: '90%' }}
                              placeholder="Select Report Type"
                              onChange={(e) => getSelectedReportData(e)}
                              value={selectedReportType}
                              allowClear={false}
                          >
                              {reportTypes && reportTypes.map((item) => (
                                  <Option key={item.reportId}>{item.reportName}</Option>
                              ))}
                          </Select>
                      </Col>
                      {
                          isDateFilterEnabled &&
                          <Col span={6}>
                              <RangePicker
                                  placeholder={["From Date", "To Date"]}
                                  style={{ width: '100%' }}
                                  format="MM/DD/YYYY"
                                  minDate={minSelectableDate}
                                  maxDate={maxSelectableDate}
                                  value={calendarRange}
                                  disabledDate={disabledDate}
                                  onCalendarChange={(dates) => {
                                      if (Array.isArray(dates)) {
                                          const [rangeStart, rangeEnd] = normalizeRange(dates[0] ?? null, dates[1] ?? null);
                                          setCalendarRange([rangeStart, rangeEnd]);
                                          setStartDate(rangeStart);
                                          setEndDate(rangeEnd);
                                      } else {
                                          setCalendarRange([null, null]);
                                          setStartDate(null);
                                          setEndDate(null);
                                      }
                                  }}
                                  onChange={(dates) => {
                                      if (Array.isArray(dates) && dates.length) {
                                          const [rangeStart, rangeEnd] = normalizeRange(dates[0] ?? null, dates[1] ?? null);
                                          setStartDate(rangeStart);
                                          setEndDate(rangeEnd);
                                          setCalendarRange([rangeStart, rangeEnd]);
                                      } else {
                                          setStartDate(null);
                                          setEndDate(null);
                                          setCalendarRange([null, null]);
                                      }
                                  }}
                              />
                          </Col>
                      }
                  </Row>
                  <Flex justify='end' style={{ marginBottom: "2rem", padding: "1rem", paddingTop: "0px", background: '#fff' }} gap='small'>
                      <Button onClick={() => search()} loading={tablePagination.loading} style={{ marginLeft: '5px' }} disabled={!(selectedReportType && (!isDateFilterEnabled || (isDateFilterEnabled && startDate && endDate)))} type="primary">
                          Search
                      </Button>
                      <Button onClick={resetFilters} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', marginLeft: '5px', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                          Reset
                      </Button>
                  </Flex>
              </Skeleton>
              {showTable &&
                <Card styles={{ body: { padding: '15px', paddingTop: '10px', height: "auto", maxHeight: reportData?.length > 0 ? "calc(100vh - 290px)" : undefined } }}>
                      {
                          reportData?.length > 0 &&
                          <Flex justify='end' style={{ paddingBottom: "5px", paddingTop: "0", alignItems: "center", background: '#fff' }} gap='small'>
                                  <Button key="export" loading={exportLoader} onClick={exportReport} style={{ background: '#1677ff', color: '#fff' }}>
                                      Export
                                  </Button>
                          </Flex>
                      }
                      {
                          reportData?.length > 0 &&
                          <div id="table-container" style={{ height: "calc(100vh - 388px)", overflow: "hidden" }}>
                              <Table
                                  scroll={{
                                      y: tableHeight,
                                      x: columns.reduce((acc, col: any) => acc + (col.width || 150), 0),
                                  }}
                                  key={tableKey}
                                  dataSource={reportData}
                                  pagination={{
                                      defaultCurrent: 1,
                                      current: tablePagination.currentPage,
                                      pageSize: tablePagination.pageSize,
                                      total: tablePagination.totalPages,
                                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                      showSizeChanger: false,
                                  }}
                                      columns={columns}
                                  onChange={tableChanges}
                                  rowKey={ primaryKey }
                                  size="small"
                                  loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: tablePagination.loading }}
                                  onHeaderRow={(columns, index) => {
                                      return {
                                          onContextMenu: (event) => {
                                              event.preventDefault();
                                              if (!menuVisible) {
                                                  document.addEventListener(`click`, function onClickOutside() {
                                                      setMenuVisible(false);
                                                      document.removeEventListener(`click`, onClickOutside);
                                                  });
                                              }
                                              setMenuVisible(true);
                                          }
                                      };
                                  }}
                              />
                          </div>
                      }
                  </Card>
              }
          </Layout>
      </ConfigProvider>
  );
};

export default Reports;
